import { Component, OnInit, output, input, inject } from '@angular/core';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntil, timer } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { ApButtonComponent } from '@ap/button';
import { AuthenticationRequest, Policy } from '@ap/fido-uaf-frontend-client';
import { ApContentService, ApContentComponent, ApContentPipe } from '@ap/content';
import { TokenTypeId, ApAuthenticatieFidoResource } from '@ap/interface-apic-authenticatie-fido';
import { ApPhonePipe } from '@ap/formatters';
import { markFormControlAsDone } from '@ap/form';
import { ApActivationCodeInputComponent } from '@ap/input/activation-code';
import { ApNotificationComponent } from '@ap/notification';
import { ApSpinnerComponent } from '@ap/spinner';

import { SignError, SignResponse } from '../../sign.model';
import { ValidationResultType } from '../../validation/validation.model';
import { ApTokenService } from '../token.service';
import { ApSubmitComponent } from '../../submit/submit.component';

import content from './phone-number.content.json';

/**
 * @ngdoc component
 * @name ApSignPhoneNumberComponent
 */
@Component({
    selector: 'ap-sign-phone-number',
    standalone: true,
    templateUrl: './phone-number.component.html',
    styleUrls: ['./phone-number.component.scss'],
    imports: [
        ApContentComponent,
        ApNotificationComponent,
        ApActivationCodeInputComponent,
        FormsModule,
        ReactiveFormsModule,
        ApButtonComponent,
        ApSpinnerComponent,
        ApSubmitComponent,
        ApContentPipe
    ]
})
export class ApSignPhoneNumberComponent implements OnInit {
    private readonly contentService = inject(ApContentService);
    private readonly tokenService = inject(ApTokenService);
    private readonly authenticationResource = inject(ApAuthenticatieFidoResource);

    /**
     * Required - Text to show on submit button
     */
    public readonly submitText = input.required<string>();

    /**
     * Required - The actionId that identifies the action to be signed
     */
    public readonly actionId = input.required<string>();

    /**
     * Required - Authentication request
     */
    public readonly authenticationRequest = input.required<AuthenticationRequest>();

    /**
     * Required - The keyId is used to re-initialize after a failed attempt
     */
    public readonly keyId = input.required<string>();

    /**
     * Text to show on cancel button
     */
    public readonly cancelText = input<string>(undefined);

    /**
     * Is user anonymous.
     * Defaults to false
     */
    public readonly anonymous = input(false);
    /**
     * Emits event when user cancels sign process
     */
    public canceled = output<void>();

    /**
     * Emits event when user finishes sign process
     */
    public done = output<void>();

    /**
     * Emits event when signing fails
     */
    public signError = output<SignError>();

    /**
     * Emits event when a retry of signing is triggered
     */
    public retry = output<string>();

    /**
     * Emits event when a restart of the signing process is triggered
     */
    public restart = output<string>();

    public phoneNumber: string;
    public signResponse = new FormControl<string>(null, [Validators.required, Validators.minLength(6)]);
    public signResponseErrors: { [key: string]: any };
    public canSign = true;
    public codeStatus: 'success' | 'error' | 'warning';
    public maxOtpCodesError = false;
    public amountOfCodesSent = 0;
    public showRetryLink = false;
    public disableResend = false;
    public hasMaskedPhoneNumber = false;
    public cooldown = null;
    public cooldownIntervalled = null;
    public cooldownInterval = 10;
    public countDown$ = null;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected readonly COOLDOWN_TIME_SECONDS = 60;
    private readonly COOLDOWN_TIME_MS = this.COOLDOWN_TIME_SECONDS * 1000;

    public ngOnInit(): void {
        this.contentService.addContentItems(content);

        const keyIds = this.tokenService.getDecodedKeyIds(this.authenticationRequest().policy, TokenTypeId.PHONE_NUMBER);

        //keyIds empty we show error here;
        const authenticationRequest = this.authenticationRequest();
        if (keyIds[0] && !this.tokenService.checkPolicyForMissingPhonenumber(authenticationRequest.policy)) {
            this.countDown$ = timer(0, 1000).pipe(takeUntil(timer(this.COOLDOWN_TIME_MS)));
            this.phoneNumber = this.getPhoneNumberFromPolicy(authenticationRequest.policy);
            this.sendCode();
            this.signResponseErrors = {
                required: this.contentService.get('phone-number.response.error.required'),
                minlength: this.contentService.get('phone-number.response.error.minlength')
            };
        }
    }

    public changePhoneNumber(): void {
        this.showRetryLink = true;
        window.open(this.contentService.get('phone-number.wrong-number.link'), '_blank', 'noreferrer, noopener');
    }

    public newNumber(): void {
        const keyId = this.tokenService.getDecodedKeyIds(this.authenticationRequest().policy, TokenTypeId.PHONE_NUMBER)[0];
        this.retry.emit(keyId);
    }

    public sign(): void {
        markFormControlAsDone(this.signResponse);
        if (this.signResponse.valid) {
            this.canSign = false;
            const response = this.signResponse.value;
            const keyId = this.tokenService.getDecodedKeyIds(this.authenticationRequest().policy, TokenTypeId.PHONE_NUMBER)[0];
            const signResponse: SignResponse = {
                keyID: keyId,
                response
            };
            this.tokenService
                .validate(this.authenticationRequest(), new Int8Array(64), TokenTypeId.PHONE_NUMBER, signResponse, this.actionId())
                .subscribe((validationResult) => {
                    if (validationResult.resultType === ValidationResultType.DONE) {
                        // TODO: The 'emit' function requires a mandatory void argument
                        this.done.emit();
                    } else {
                        this.signError.emit({
                            key: validationResult.reason,
                            type: 'error'
                        });
                        this.signResponse.reset();

                        if (validationResult.resultType === ValidationResultType.PROCESS_RETRY) {
                            this.retry.emit(keyId);
                        } else if (validationResult.resultType === ValidationResultType.COMPONENT_RETRY) {
                            this.canSign = true;
                        }
                    }
                });
        }
    }

    public cancel(): void {
        // TODO: The 'emit' function requires a mandatory void argument
        this.canceled.emit();
    }

    public sendCode(): void {
        this.disableResend = true;
        const keyIds = this.tokenService.getKeyIds(this.authenticationRequest().policy, TokenTypeId.PHONE_NUMBER);
        this.authenticationResource
            .postOneAuthenticationRequestOtpMessage({
                aaid: TokenTypeId.PHONE_NUMBER,
                key_id: keyIds[0],
                authentication_task_id: this.actionId(),
                challenge: this.authenticationRequest().challenge
            })
            .subscribe({
                error: (response: HttpErrorResponse) => {
                    this.codeStatus = 'error';
                    this.disableResend = false;
                    this.maxOtpCodesError = response?.error?.code === 'LSM23046';
                },
                complete: () => {
                    this.amountOfCodesSent++;
                    this.codeStatus = 'success';
                    if (this.amountOfCodesSent >= 4) {
                        this.codeStatus = 'warning';
                        this.maxOtpCodesError = true;
                    }
                    if (!this.cooldown) {
                        this.disableResend = false;
                        this.cooldown = this.COOLDOWN_TIME_SECONDS;
                    } else {
                        this.countDown$.subscribe((time) => {
                            if (this.cooldown % this.cooldownInterval === 0) {
                                this.cooldownIntervalled = this.cooldown;
                            }
                            this.cooldown = this.COOLDOWN_TIME_SECONDS - time;
                            if (this.cooldown === 1) {
                                this.disableResend = false;
                            }
                        });
                    }
                }
            });
    }

    public getPhoneNumberFromPolicy(policy: Policy): string {
        let phoneNumberMetadata = this.tokenService.getPhoneNumberMetadata(policy);

        if (phoneNumberMetadata && phoneNumberMetadata.maskedPhoneNumber) {
            this.hasMaskedPhoneNumber = true;
            return phoneNumberMetadata.maskedPhoneNumber;
        }
        return new ApPhonePipe().transform(
            this.tokenService.getDecodedKeyIds(this.authenticationRequest().policy, TokenTypeId.PHONE_NUMBER)[0]
        );
    }

    protected get notificationContent(): string {
        if (this.maxOtpCodesError && this.codeStatus) {
            return this.contentService.get(`phone-number.code.error.max-otp-for-task-reached`) || '';
        }
        return (
            ((this.codeStatus === 'success' || this.codeStatus === 'error') &&
                this.contentService.get(`phone-number.code.${this.codeStatus}`)) ||
            ''
        );
    }

    protected get disabledContent(): string {
        return `${this.cooldownIntervalled === this.COOLDOWN_TIME_SECONDS ? `${this.notificationContent}.` : ''} ${this.contentService.get('phone-number.enable-resend-button-in', { seconds: this.cooldownIntervalled })}`;
    }
}
